import React, { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Loader, X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import useGeoLocation from 'react-ipgeolocation';
import { usePriorityAccessModal } from '../../contexts/priority-access-modal-context';
import { useFormTracking } from '../../hooks/useAnalytics';
import Analytics from '../../lib/analytics';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: z.string().optional(),
  companyName: z.string().min(1, 'Company name is required'),
  role: z.string().min(1, 'Role is required'),
  intent: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export const PriorityAccessModal: React.FC = () => {
  const { isOpen, closeModal } = usePriorityAccessModal();
  const formRef = useRef<HTMLFormElement>(null);
  const geoLocation = useGeoLocation();
  const [phoneValue, setPhoneValue] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('in');
  const userHasSelectedCountryRef = useRef(false); // Use ref to avoid stale closures

  // Debug: Track all country changes with timestamps
  const logCountryChange = (
    source: string,
    country: string,
    userSelected: boolean
  ) => {
    console.log(
      `🕐 ${new Date().toISOString().split('T')[1]} [${source}] Country: ${selectedCountry} → ${country}, UserSelected: ${userSelected}`
    );
  };
  // const [dropdownOpen, setDropdownOpen] = useState(false); // Commented out as callbacks were removed
  const [submissionState, setSubmissionState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  // Initialize form tracking
  const { trackFormSubmit, trackFormSuccess, trackFormError } =
    useFormTracking('priority_access');

  // Add custom scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .modal-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      
      .modal-scrollbar::-webkit-scrollbar-track {
        background: rgba(30, 64, 175, 0.3);
        border-radius: 4px;
      }
      
      .modal-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(147, 197, 253, 0.8);
        border-radius: 4px;
        border: 1px solid rgba(30, 64, 175, 0.5);
      }
      
      .modal-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(147, 197, 253, 1);
      }
      
      
      /* Style for better country code visibility */
      .react-tel-input .selected-flag .flag {
        margin-right: 6px !important;
      }

      /* Remove scrollbars from dropdown but keep scrollability */
      .react-tel-input .country-list {
        overflow-x: hidden !important;
        overflow-y: auto !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }

      .react-tel-input .country-list::-webkit-scrollbar {
        display: none !important;
      }
      
      /* Focus and scroll management for dropdown */
      .react-tel-input .country-list:focus-within {
        outline: none !important;
      }
      
      .react-tel-input .country-list .country:hover {
        background: rgba(59, 130, 246, 0.05) !important;
        color: rgba(59, 130, 246, 1) !important;
      }
      
      /* Make phone input take full width of parent container */
      .react-tel-input .form-control {
        width: 100% !important;
        box-sizing: border-box !important;
      }
      
      .react-tel-input {
        width: 100% !important;
      }
      
      .react-tel-input .country-list .country.highlight {
        background: rgba(59, 130, 246, 0.1) !important;
        color: rgba(59, 130, 246, 1) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Country name to country code mapping for geolocation
  const countryNameToCode: { [key: string]: string } = {
    'United States': 'us',
    'United Kingdom': 'gb',
    Canada: 'ca',
    Australia: 'au',
    India: 'in',
    Germany: 'de',
    France: 'fr',
    Italy: 'it',
    Spain: 'es',
    Netherlands: 'nl',
    Belgium: 'be',
    Switzerland: 'ch',
    Austria: 'at',
    Sweden: 'se',
    Norway: 'no',
    Denmark: 'dk',
    Finland: 'fi',
    Brazil: 'br',
    Mexico: 'mx',
    Argentina: 'ar',
    Chile: 'cl',
    Colombia: 'co',
    Peru: 'pe',
    China: 'cn',
    Japan: 'jp',
    'South Korea': 'kr',
    Singapore: 'sg',
    Malaysia: 'my',
    Thailand: 'th',
    Philippines: 'ph',
    Indonesia: 'id',
    Vietnam: 'vn',
    'South Africa': 'za',
    Egypt: 'eg',
    Nigeria: 'ng',
    Kenya: 'ke',
    Russia: 'ru',
    Poland: 'pl',
    'Czech Republic': 'cz',
    Hungary: 'hu',
    Romania: 'ro',
    Bulgaria: 'bg',
    Turkey: 'tr',
    Israel: 'il',
    'United Arab Emirates': 'ae',
    'Saudi Arabia': 'sa',
    Pakistan: 'pk',
    Bangladesh: 'bd',
    'Sri Lanka': 'lk',
    'New Zealand': 'nz',
  };

  // Get initial country for phone input (react-phone-input-2 uses lowercase codes)
  const getInitialCountry = () => {
    if (geoLocation && 'country' in geoLocation && geoLocation.country) {
      const countryCode = countryNameToCode[(geoLocation as any).country];
      return countryCode || 'in'; // fallback to India
    }
    return 'in'; // default to India
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Set initial country when geolocation data is available (only if user hasn't manually selected)
  useEffect(() => {
    console.log(
      '🌍 Geolocation effect triggered. UserSelected:',
      userHasSelectedCountryRef.current,
      'GeoData:',
      !!geoLocation
    );
    if (
      geoLocation &&
      'country' in geoLocation &&
      geoLocation.country &&
      !userHasSelectedCountryRef.current
    ) {
      const countryCode = getInitialCountry();
      logCountryChange('geolocation', countryCode, false);
      console.log(
        '🌍 Applying geolocation country:',
        countryCode,
        'userSelected:',
        userHasSelectedCountryRef.current
      );
      setSelectedCountry(countryCode);
      // Reset phone value when country changes programmatically
      setPhoneValue(''); // Clear phone when country auto-detected

      console.log('🌍 IP Geolocation Data (applied):');
      console.log('- Country Name:', (geoLocation as any).country);
      console.log('- Country Code:', countryCode);
      console.log('- Full Geolocation Object:', geoLocation);
    } else if (userHasSelectedCountryRef.current) {
      console.log(
        '🚫 Geolocation ignored - user has manually selected country'
      );
    } else {
      console.log('⚠️ No geolocation data available yet');
    }
  }, [geoLocation]); // Removed userHasSelectedCountry from dependencies

  // Handle scroll management when dropdown is open
  // Note: dropdownOpen callbacks were removed from PhoneInput due to type incompatibility
  // useEffect(() => {
  //   if (dropdownOpen) {
  //     // Prevent modal scroll when dropdown is open
  //     const modalElement = document.querySelector(
  //       '.modal-scrollbar'
  //     ) as HTMLElement;
  //     if (modalElement) {
  //       modalElement.style.overflow = 'hidden';
  //     }
  //   } else {
  //     // Restore modal scroll when dropdown is closed
  //     const modalElement = document.querySelector(
  //       '.modal-scrollbar'
  //     ) as HTMLElement;
  //     if (modalElement) {
  //       modalElement.style.overflow = 'auto';
  //     }
  //   }

  //   return () => {
  //     // Cleanup: restore modal scroll
  //     const modalElement = document.querySelector(
  //       '.modal-scrollbar'
  //     ) as HTMLElement;
  //     if (modalElement) {
  //       modalElement.style.overflow = 'auto';
  //     }
  //   };
  // }, [dropdownOpen]);

  // Track modal open when it becomes visible
  useEffect(() => {
    if (isOpen) {
      Analytics.trackModal('modal_open', { trigger_location: 'navbar' });
    }
  }, [isOpen]);

  const onSubmit = async (data: FormData) => {
    trackFormSubmit();
    setSubmissionState('loading');

    try {
      const formData = new FormData();

      // Map form fields to Google Forms entry IDs
      const fullName = `${data.firstName} ${data.lastName}`;

      // Extract phone number and country code from phoneValue
      const fullPhoneNumber = phoneValue || data.phoneNumber || '';
      const phonePrefix =
        fullPhoneNumber.length > 10 ? fullPhoneNumber.slice(0, -10) : '+91';
      const phoneNumberOnly =
        fullPhoneNumber.length > 10
          ? fullPhoneNumber.slice(-10)
          : fullPhoneNumber;

      // Use geolocation country for country field
      const country =
        geoLocation && 'country' in geoLocation
          ? (geoLocation as any).country
          : 'India';

      formData.append('entry.790617379', fullName);
      formData.append('entry.463891999', data.companyName);
      formData.append('entry.228426543', data.role);
      formData.append('entry.97498959', data.email);
      formData.append('entry.129709222', data.intent || '');
      formData.append('entry.1564397805', country);
      formData.append('entry.1170165529', phonePrefix);
      formData.append('entry.1255567281', phoneNumberOnly);

      // Submit to Google Forms
      await fetch(
        'https://docs.google.com/forms/d/e/1FAIpQLSfhuxIWcgLzIrAN05VQDu0K8U8ZEGiRoYkg-CWaLltC-06SFg/formResponse',
        {
          method: 'POST',
          body: formData,
          mode: 'no-cors',
        }
      );

      // Show success state
      setSubmissionState('success');
      trackFormSuccess();

      // Auto-dismiss modal after 3 seconds on success
      setTimeout(() => {
        reset();
        setPhoneValue('');
        setSubmissionState('idle');
        closeModal();
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmissionState('error');
      trackFormError('submission_failed');

      // Return to form after showing error for a moment (form state preserved)
      setTimeout(() => {
        setSubmissionState('idle');
      }, 3000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onWheel={e => e.stopPropagation()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="modal-scrollbar relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 p-8 shadow-2xl"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(147, 197, 253, 0.8) rgba(30, 64, 175, 0.3)',
            }}
            onWheel={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="mb-8 text-center relative">
              <button
                onClick={closeModal}
                className="absolute top-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-200 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </button>
              <h2 className="mb-3 text-3xl font-bold text-white">
                Get Priority Access
              </h2>
              <p className="text-lg text-blue-100">
                Limited early-access seats available, secure yours today.
              </p>
            </div>

            {/* Form or Status Content */}
            {submissionState === 'idle' ? (
              <form
                ref={formRef}
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {/* Name Fields */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      First Name *
                    </label>
                    <input
                      {...register('firstName')}
                      className="w-full rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      Last Name *
                    </label>
                    <input
                      {...register('lastName')}
                      className="w-full rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email and Phone Number Row */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Email */}
                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="ch-no-capture w-full rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Phone Number - Unified PhoneInput */}
                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      Phone Number <span className="text-blue-200">(optional)</span>
                    </label>
                    <PhoneInput
                      country={selectedCountry}
                      value={phoneValue}
                      onChange={(phone, countryData) => {
                        console.log('📞 Phone Input Change:', {
                          phone,
                          countryData,
                        });
                        setPhoneValue(phone);
                        setValue('phoneNumber', phone);

                        // Track country changes - both manual dropdown and auto-detection
                        if (
                          countryData &&
                          typeof countryData === 'object' &&
                          (countryData as any).countryCode
                        ) {
                          const newCountry = (
                            countryData as any
                          ).countryCode.toLowerCase();
                          if (newCountry !== selectedCountry) {
                            console.log(
                              '👤 Priority Access: Country change detected:',
                              selectedCountry,
                              '->',
                              newCountry
                            );
                            userHasSelectedCountryRef.current = true;
                            setSelectedCountry(newCountry);
                          }
                        }
                      }}
                      placeholder="Enter phone number"
                      enableSearch={true}
                      searchPlaceholder="Search countries..."
                      countryCodeEditable={true}
                      enableClickOutside={true}
                      disableDropdown={false}
                      containerClass="form-group"
                      inputClass="ch-no-capture form-control"
                      buttonClass="btn btn-outline-secondary dropdown-toggle"
                      dropdownClass="dropdown-menu"
                      searchClass="form-control"
                    />
                    {errors.phoneNumber && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.phoneNumber.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Company Info */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      Company Name *
                    </label>
                    <input
                      {...register('companyName')}
                      className="w-full rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                      placeholder="Enter your company name"
                    />
                    {errors.companyName && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.companyName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-medium text-white">
                      Role *
                    </label>
                    <input
                      {...register('role')}
                      className="w-full rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                      placeholder="Enter your role"
                    />
                    {errors.role && (
                      <p className="mt-2 rounded-lg bg-red-500/20 px-3 py-1 text-sm text-red-100">
                        {errors.role.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Intent */}
                <div>
                  <label className="mb-3 block text-sm font-medium text-white">
                    Intent
                  </label>
                  <textarea
                    {...register('intent')}
                    rows={4}
                    className="w-full resize-none rounded-xl border border-white/20 bg-white/95 px-4 py-4 text-gray-900 placeholder-gray-500 shadow-lg backdrop-blur-sm transition-all duration-200 outline-none focus:border-white/50 focus:ring-2 focus:ring-white/50"
                    placeholder="Tell us more about your data needs or any specific requirements..."
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full transform rounded-xl bg-blue-400 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:bg-blue-500 hover:shadow-xl focus:ring-2 focus:ring-white/50 focus:outline-none disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    Submit Priority Access Request
                  </button>
                </div>
              </form>
            ) : (
              /* Status Content - replaces the entire form */
              <div className="flex min-h-[400px] items-center justify-center">
                <AnimatePresence mode="wait">
                  {/* Loading State */}
                  {submissionState === 'loading' && (
                    <motion.div
                      key="loading"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      className="flex flex-col items-center space-y-4"
                    >
                      <Loader
                        className="h-16 w-16 animate-spin text-white"
                        strokeWidth={2}
                      />
                    </motion.div>
                  )}

                  {/* Success State */}
                  {submissionState === 'success' && (
                    <motion.div
                      key="success"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 20,
                      }}
                      className="flex flex-col items-center space-y-4"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: 0.2,
                          type: 'spring',
                          stiffness: 200,
                          damping: 20,
                        }}
                      >
                        <CheckCircle
                          className="h-16 w-16 text-green-400"
                          strokeWidth={2}
                        />
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-center text-lg font-semibold text-white"
                      >
                        Successfully submitted
                      </motion.p>
                    </motion.div>
                  )}

                  {/* Error State */}
                  {submissionState === 'error' && (
                    <motion.div
                      key="error"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 20,
                      }}
                      className="flex flex-col items-center space-y-4"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: 0.2,
                          type: 'spring',
                          stiffness: 200,
                          damping: 20,
                        }}
                      >
                        <XCircle
                          className="h-16 w-16 text-red-400"
                          strokeWidth={2}
                        />
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-center text-lg font-semibold text-white"
                      >
                        Sorry, could not submit. Please try again.
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
